import { useEffect, useMemo, useRef, useState } from "react";

const PLATFORM_ORDER = ["iphone", "ipad", "mac", "watch", "android"];
const PLATFORM_LABELS = {
  iphone: "iPhone",
  ipad: "iPad",
  mac: "Mac",
  watch: "Apple Watch",
  android: "Android",
};

function orderedPlatforms(items) {
  const seen = [...new Set(items.map((item) => item.platform || "other"))];
  return [
    ...PLATFORM_ORDER.filter((value) => seen.includes(value)),
    ...seen.filter((value) => !PLATFORM_ORDER.includes(value)),
  ];
}

export default function ScreenshotGallery({
  items = [],
  appName = "",
  title = "Screens",
  countLabel = "discovered screens",
  platformLabel = "Platform",
  embed = false,
}) {
  const shots = useMemo(
    () => (Array.isArray(items) ? items.filter((item) => item && item.url) : []),
    [items],
  );
  const platforms = useMemo(() => orderedPlatforms(shots), [shots]);
  const showPlatformSelect = platforms.length > 1;
  const [platform, setPlatform] = useState(platforms[0] || "");
  const [open, setOpen] = useState(null);
  const [failedImages, setFailedImages] = useState(() => new Set());
  const closeRef = useRef(null);
  const openTrigger = useRef(null);

  useEffect(() => {
    setPlatform((current) => (platforms.includes(current) ? current : platforms[0] || ""));
  }, [platforms]);

  const visible = useMemo(
    () => (showPlatformSelect ? shots.filter((item) => (item.platform || "other") === platform) : shots),
    [shots, platform, showPlatformSelect],
  );

  const modalOpen = open != null;
  useEffect(() => {
    if (!modalOpen) return undefined;
    closeRef.current?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(null);
      if (event.key === "ArrowRight") {
        event.preventDefault();
        setOpen((index) => Math.min(visible.length - 1, (index ?? 0) + 1));
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setOpen((index) => Math.max(0, (index ?? 0) - 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      openTrigger.current?.focus();
    };
  }, [modalOpen, visible.length]);

  if (!shots.length) return null;
  const current = open != null ? visible[open] : null;

  const platformSelect = showPlatformSelect ? (
    <label className="compact-select">
      <span>{platformLabel}</span>
      <select
        value={platform}
        onChange={(event) => {
          setPlatform(event.target.value);
          setOpen(null);
        }}
      >
        {platforms.map((value) => (
          <option key={value} value={value}>
            {PLATFORM_LABELS[value] || value}
          </option>
        ))}
      </select>
    </label>
  ) : null;

  return (
    <>
      {embed ? (
        platformSelect ? <div className="app-screens__tools">{platformSelect}</div> : null
      ) : (
        <div className="app-section__head">
          <h2 id="screens">{title}</h2>
          {platformSelect}
        </div>
      )}
      <p className="muted">{shots.length} {countLabel}</p>
      <div className="shot-rail">
        {visible.map((shot, index) => (
          <figure className="shot" key={shot.media_id || shot.url}>
            <button
              type="button"
              className="shot__open"
              aria-haspopup="dialog"
              aria-controls="screenshot-viewer"
              onClick={(event) => { openTrigger.current = event.currentTarget; setOpen(index); }}
              aria-label={shot.alt || `${appName} screenshot ${index + 1}`}
            >
              <img
                src={shot.url}
                alt={shot.alt || `${appName} screenshot ${index + 1}`}
                loading={index === 0 ? "eager" : "lazy"}
                onError={(event) => {
                  const key = shot.media_id || shot.url;
                  setFailedImages((current) => new Set(current).add(key));
                }}
              />
            </button>
            {failedImages.has(shot.media_id || shot.url) ? (
              <figcaption className="muted" role="status">Screenshot unavailable</figcaption>
            ) : null}
          </figure>
        ))}
      </div>
      {current ? (
        <div id="screenshot-viewer" className="shot-lightbox" role="dialog" aria-modal="true" aria-label={`${appName} screenshot viewer`} onClick={() => setOpen(null)} onKeyDown={(event) => {
          if (event.key !== "Tab") return;
          const buttons = [...event.currentTarget.querySelectorAll("button")];
          const first = buttons[0];
          const last = buttons[buttons.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }}>
          <button ref={closeRef} type="button" className="shot-lightbox__close" onClick={() => setOpen(null)} aria-label="Close screenshot viewer">×</button>
          <button type="button" className="shot-lightbox__prev" onClick={(event) => { event.stopPropagation(); setOpen((index) => Math.max(0, (index ?? 0) - 1)); }} aria-label="Previous screenshot">‹</button>
          <img src={current.url} alt={current.alt || appName} onClick={(event) => event.stopPropagation()} />
          <button type="button" className="shot-lightbox__next" onClick={(event) => { event.stopPropagation(); setOpen((index) => Math.min(visible.length - 1, (index ?? 0) + 1)); }} aria-label="Next screenshot">›</button>
        </div>
      ) : null}
    </>
  );
}
